<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  LICENSE_TYPES,
  STATUS_LABELS,
  STATUS_TAG,
  createDriver,
  deleteDriver,
  listDrivers,
  updateDriver,
  type Driver,
  type DriverInput,
} from '../api/drivers'

const rows = ref<Driver[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Driver | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  code: '',
  name: '',
  phone: '',
  license_type: 'A1' as (typeof LICENSE_TYPES)[number],
  status: 'active' as Driver['status'],
})

const codeError = computed(() => (form.code.trim() ? '' : '请输入司机工号'))

const formErrorText = computed(() => {
  if (codeError.value) return codeError.value
  if (!form.license_type) return '请选择准驾车型'
  if (!form.status) return '请选择司机状态'
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listDrivers({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    phone: '',
    license_type: 'A1' as (typeof LICENSE_TYPES)[number],
    status: 'active' as Driver['status'],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Driver): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    phone: row.phone,
    license_type: row.license_type as (typeof LICENSE_TYPES)[number],
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: DriverInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    phone: form.phone.trim(),
    license_type: form.license_type,
    status: form.status,
  }
  try {
    if (editing.value) await updateDriver(editing.value.id, input)
    else await createDriver(input)
    formOpen.value = false
    await load(editing.value ? '司机已更新' : '司机已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Driver): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteDriver(row.id)
    await load(`已删除司机「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-drv">
    <header class="yc-drv__bar">
      <el-input
        v-model="query.keyword"
        class="yc-drv__keyword"
        placeholder="工号 / 姓名 / 电话"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-drv__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'dsp:driver:create'" type="primary" @click="openCreate">新建司机</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-drv__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-drv__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-drv__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="工号" min-width="120" />
        <el-table-column prop="name" label="姓名" min-width="120" show-overflow-tooltip />
        <el-table-column prop="phone" label="电话" min-width="145" show-overflow-tooltip />
        <el-table-column prop="license_type" label="准驾车型" width="110">
          <template #default="{ row }">{{ row.license_type || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as Driver['status']] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as Driver['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'dsp:driver:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该司机？已被车辆或行车任务引用的司机不能删除。" width="280" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'dsp:driver:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有司机"
        description="行车任务要挂司机，先建一个在岗司机档案。"
      >
        <el-button v-perm="'dsp:driver:create'" type="primary" @click="openCreate">新建司机</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-drv__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑司机 · ${editing.code}` : '新建司机'"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-drv__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="工号" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 SJ001" />
        </el-form-item>

        <el-form-item label="姓名">
          <el-input v-model="form.name" maxlength="64" placeholder="如 王大车" />
        </el-form-item>

        <el-form-item label="电话">
          <el-input v-model="form.phone" maxlength="32" placeholder="如 13800000000" />
        </el-form-item>

        <el-form-item label="准驾车型">
          <el-select v-model="form.license_type" class="yc-drv__full">
            <el-option v-for="t in LICENSE_TYPES" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">在岗</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-drv {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__status {
    width: 140px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
